// ─── calcUtils.js ─────────────────────────────────────────────────────────────
// Single source of truth for surface area calculations.
// Used by: SurfaceBlock (display), ProjectDetail (summary), ScopeReport (PDF),
// ExcelExport (formulas). All must produce consistent numbers.

export function n(val, fb = 0) {
  const v = parseFloat(val);
  return isNaN(v) ? fb : v;
}

// Calculate the m² (or LM) for a single surface entry
export function entryM2(substrate, e) {
  const base = e.baseSurface || substrate;
  const l = n(e.length), h = n(e.height), w = n(e.width), q = n(e.quantity, 1);
  const sides = e.sides === 'both' ? 2 : 1;

  switch (base) {
    case 'Doors': {
      const dw = n(e.doorWidth, 0.9), dh = n(e.doorHeight, 2.1);
      let m2 = dw && dh ? dw * dh * q * sides : 0;
      // Add frame area when included: perimeter × frame width × qty × sides
      if (e.includeFrame) {
        const fw = n(e.frameWidth, 0.05);
        m2 += 2 * (dw + dh) * fw * q * sides;
      }
      return m2;
    }
    case 'Windows': {
      const fw = n(e.frameWidth), fl = n(e.frameLength);
      if (e.windowMeasureType === 'frame') return fw && fl ? (2 * fw + 2 * fl) * q : 0;
      let m2 = fw && fl ? fw * fl * q : 0;
      // Add frame perimeter × frame width when included (m² mode only)
      if (e.includeFrame && fw && fl) {
        const wfw = n(e.windowFrameWidth, 0.05);
        m2 += 2 * (fw + fl) * wfw * q;
      }
      return m2;
    }
    case 'Skirting':
    case 'Fascia':
    case 'Gutter':
    case 'Downpipes':
    case 'Slab Edge':
      return l * q;
    case 'Posts':
      return h * q;
    case 'Stairs': {
      const sw = w || h; // width preferred, fall back to height for legacy entries
      let t = l && sw ? l * sw * q : 0;
      if (e.includeLanding && n(e.landingLength) && n(e.landingWidth)) t += n(e.landingLength) * n(e.landingWidth);
      return t;
    }
    default: {
      if (l && h) return l * h * q;
      if (l && w) return l * w * q;
      if (h && w) return h * w * q;
      if (l) return l * q;
      return 0;
    }
  }
}

// Unit label for a surface type
const LINEAR_UNIT_SURFACES = new Set(['Skirting', 'Gutter', 'Fascia', 'Downpipes', 'Slab Edge']);
export function surfaceUnit(substrate, baseSurface) {
  const base = baseSurface || substrate;
  if (LINEAR_UNIT_SURFACES.has(base)) return 'm';
  return 'm²';
}

// Total m² for a list of entries on one substrate
export function surfaceTotal(substrate, entries, girth = 1) {
  return entries.reduce((s, e) => s + entryM2(substrate, e), 0) * girth;
}

// Check if an entry has any measurement data
export function hasData(e) {
  return n(e.length) > 0 || n(e.height) > 0 || n(e.width) > 0 || n(e.doorWidth) > 0 || n(e.frameWidth) > 0;
}

// Check if the user has actually entered measurements (not just defaults from emptyEntry)
// Doors default to 0.9×2.1 — that doesn't count as "measured" for auto-finish
export function isUserMeasured(substrate, e) {
  const base = e.baseSurface || substrate;
  switch (base) {
    case 'Doors':
      // Doors have pre-filled defaults — consider measured if user changed dims, qty, or sides
      return (n(e.doorWidth) !== 0.9 || n(e.doorHeight) !== 2.1 || n(e.quantity, 1) > 1 || e.sides === 'both');
    case 'Windows':
      return n(e.frameWidth) > 0 && n(e.frameLength) > 0;
    default:
      // Standard surfaces start with empty fields — any non-zero dimension means user typed something
      return entryM2(substrate, e) > 0;
  }
}

// Calculate total across all substrates for a set of entries, applying repeat group multipliers and girth
export function calcAreaTotal(area) {
  const entries = area.surfaceEntries || [];
  const groups = area.repeatGroups || [];
  const gc = {};
  groups.forEach(g => { gc[g.id] = g.count || 1; });

  const bySubstrate = {};
  entries.forEach(e => {
    const k = e.substrate || '';
    if (!k) return;
    if (!bySubstrate[k]) bySubstrate[k] = [];
    bySubstrate[k].push(e);
  });

  let total = 0;
  Object.entries(bySubstrate).forEach(([sub, subEntries]) => {
    const base = area.surfaceSettings?.[sub]?.baseSurface || sub;
    const girth = area.surfaceSettings?.[sub]?.girth ?? DEFAULT_GIRTHS[base] ?? 1;
    subEntries.forEach(e => {
      const rc = e.repeatGroupId ? (gc[e.repeatGroupId] || 1) : 1;
      total += entryM2(sub, e) * girth * rc;
    });
  });
  return total;
}

// ── Production calculations (mirrors Excel template formulas) ────────────────
// H (Hours) = qty*coats/rate + prep_hours
// N (Prep hours) = qty/prep_rate
// J (Litres) = qty*coats*girth/spread_rate
// Downtime hours = downtime% * total_surface_hours

const DEFAULT_SPREAD_RATE = 12;
const DEFAULT_RATES = {
  'Ceilings': 16, 'Walls': 16, 'Floors': 16,
  'Windows': 10, 'Windows (LM)': 10, 'Windows (M2)': 10,
  'Doors': 7, 'Door frames': 7, 'Skirting': 10, 'Slab Edge': 10,
  'Stairs': 12, 'Gutter': 12, 'Fascia': 16, 'Soffit': 16,
  'Downpipes': 16, 'Balustrades/Handrails': 7, 'Balustrade / Handrails': 7, 'Posts': 16, 'Roofs': 16,
  // Legacy names for backward compat
  'Floor': 16, 'Eaves': 16, 'Walls - Flat': 16, 'Walls Rough': 10,
};
export const DEFAULT_GIRTHS = {
  // Internal
  'Windows (LM)': 0.2, 'Windows': 0.2, 'Door frames': 0.3, 'Skirting': 0.2,
  // External
  'Gutter': 0.3, 'Fascia': 0.3, 'Eaves': 0.8, 'Soffit': 1.0,
  'Downpipes': 0.3, 'Slab Edge': 0.15,
  'Posts': 0.3,
  'Roofs': 1.15,
};

export function calcProductionSummary(areas) {
  let totalIntHours = 0, totalExtHours = 0;
  let totalIntLitres = 0, totalExtLitres = 0;
  let totalIntM2 = 0, totalExtM2 = 0;
  let totalIntDowntimeHours = 0, totalExtDowntimeHours = 0;

  (areas || []).forEach(area => {
    const gc = {};
    (area.repeatGroups || []).forEach(g => { gc[g.id] = g.count || 1; });
    const isExt = area.type === 'external';

    // Group entries by substrate
    const bySubstrate = {};
    (area.surfaceEntries || []).forEach(e => {
      const k = e.substrate; if (!k) return;
      if (!bySubstrate[k]) bySubstrate[k] = [];
      bySubstrate[k].push(e);
    });

    let areaHours = 0, areaLitres = 0, areaM2 = 0;

    Object.entries(bySubstrate).forEach(([sub, entries]) => {
      const settings = area.surfaceSettings?.[sub] || {};
      const base = settings.baseSurface || sub;
      const rate = settings.paintingSpeed ?? DEFAULT_RATES[base] ?? 20;
      const girth = settings.girth ?? DEFAULT_GIRTHS[base] ?? 1;
      const coats = settings.coats ?? (base === 'Doors' || base === 'Door frames' ? 3 : 2);
      const prepRate = settings.prepRate ?? 75;

      // Method downtime (spray/abseil setup time as % of base hours)
      const methodPrep = settings.methodPrep ?? 0;

      entries.forEach(e => {
        const rc = e.repeatGroupId ? (gc[e.repeatGroupId] || 1) : 1;
        const m2 = entryM2(sub, e) * rc;
        const qty = m2 * girth; // effective painted qty
        const paintHours = qty * coats / rate;
        const prepHours = prepRate > 0 ? qty / prepRate : 0;
        const methodPrepHours = (paintHours + prepHours) * methodPrep;
        const hours = paintHours + prepHours + methodPrepHours;
        const litres = qty * coats / DEFAULT_SPREAD_RATE;

        areaHours += hours;
        areaLitres += litres;
        areaM2 += m2;
      });
    });

    const downtimePct = (area.downtime ?? 10) / 100;
    const downtimeHours = areaHours * downtimePct;

    if (isExt) {
      totalExtHours += areaHours;
      totalExtLitres += areaLitres;
      totalExtM2 += areaM2;
      totalExtDowntimeHours += downtimeHours;
    } else {
      totalIntHours += areaHours;
      totalIntLitres += areaLitres;
      totalIntM2 += areaM2;
      totalIntDowntimeHours += downtimeHours;
    }
  });

  const totalHours = totalIntHours + totalExtHours;
  const totalDowntime = totalIntDowntimeHours + totalExtDowntimeHours;

  return {
    internal: { hours: totalIntHours, litres: totalIntLitres, m2: totalIntM2, downtime: totalIntDowntimeHours },
    external: { hours: totalExtHours, litres: totalExtLitres, m2: totalExtM2, downtime: totalExtDowntimeHours },
    totalHours,
    totalDowntime,
    grandTotalHours: totalHours + totalDowntime,
    totalLitres: totalIntLitres + totalExtLitres,
  };
}

// ── Per-area production estimate ────────────────────────────────────────────
export function calcAreaHours(area) {
  const gc = {};
  const groupNames = {};
  (area.repeatGroups || []).forEach(g => { gc[g.id] = g.count || 1; groupNames[g.id] = g.name || 'Group'; });
  const bySubstrate = {};
  (area.surfaceEntries || []).forEach(e => {
    const k = e.substrate; if (!k) return;
    if (!bySubstrate[k]) bySubstrate[k] = [];
    bySubstrate[k].push(e);
  });
  let hours = 0, litres = 0;
  const groupHours = {}; // groupId → hours
  Object.entries(bySubstrate).forEach(([sub, entries]) => {
    const s = area.surfaceSettings?.[sub] || {};
    const base = s.baseSurface || sub;
    const rate = s.paintingSpeed ?? DEFAULT_RATES[base] ?? 20;
    const girth = s.girth ?? DEFAULT_GIRTHS[base] ?? 1;
    const coats = s.coats ?? (base === 'Doors' || base === 'Door frames' ? 3 : 2);
    const prep = s.prepRate ?? 75;
    const methodPrep = s.methodPrep ?? 0;
    entries.forEach(e => {
      const rc = e.repeatGroupId ? (gc[e.repeatGroupId] || 1) : 1;
      const qty = entryM2(sub, e) * rc * girth;
      const paintH = qty * coats / rate;
      const prepH = prep > 0 ? qty / prep : 0;
      const methodPrepH = (paintH + prepH) * methodPrep;
      const h = paintH + prepH + methodPrepH;
      hours += h;
      litres += qty * coats / DEFAULT_SPREAD_RATE;
      if (e.repeatGroupId) {
        groupHours[e.repeatGroupId] = (groupHours[e.repeatGroupId] || 0) + h;
      }
    });
  });

  // PM-added surfaces — assessed hours only (no measurements exist)
  let pmAddedHours = 0;
  const pmAdded = area.pmAddedSurfaces || [];
  const assessData = area.assessBySubstrate || {};
  pmAdded.forEach(sub => {
    if (!bySubstrate[sub]) { // only count if not already measured
      const ah = parseFloat(assessData[sub]?.hours);
      if (ah > 0) pmAddedHours += ah;
    }
  });

  const dt = hours * ((area.downtime ?? 10) / 100);
  // Build compound breakdown: [{ name, hours }]
  const groupBreakdown = Object.entries(groupHours)
    .filter(([, h]) => h > 0)
    .map(([id, h]) => ({ name: groupNames[id] || 'Group', hours: h }));
  return { hours, downtime: dt, total: hours + dt + pmAddedHours, litres, groupBreakdown, pmAddedHours };
}

// ── Per-substrate breakdown (used by Review Mode) ────────────────────────────
export function calcSubstrateBreakdown(area) {
  const gc = {};
  (area.repeatGroups || []).forEach(g => { gc[g.id] = g.count || 1; });
  const bySubstrate = {};
  (area.surfaceEntries || []).forEach(e => {
    const k = e.substrate; if (!k) return;
    if (!bySubstrate[k]) bySubstrate[k] = [];
    bySubstrate[k].push(e);
  });
  const result = {};
  Object.entries(bySubstrate).forEach(([sub, entries]) => {
    const s = area.surfaceSettings?.[sub] || {};
    const base = s.baseSurface || sub;
    const rate = s.paintingSpeed ?? DEFAULT_RATES[base] ?? 20;
    const girth = s.girth ?? DEFAULT_GIRTHS[base] ?? 1;
    const coats = s.coats ?? (base === 'Doors' || base === 'Door frames' ? 3 : 2);
    const prep = s.prepRate ?? 75;
    const methodPrep = s.methodPrep ?? 0;
    let m2 = 0, hours = 0, litres = 0;
    entries.forEach(e => {
      const rc = e.repeatGroupId ? (gc[e.repeatGroupId] || 1) : 1;
      const em2 = entryM2(sub, e) * rc;
      const qty = em2 * girth;
      const paintH = qty * coats / rate;
      const prepH = prep > 0 ? qty / prep : 0;
      const methodPrepH = (paintH + prepH) * methodPrep;
      hours += paintH + prepH + methodPrepH;
      litres += qty * coats / DEFAULT_SPREAD_RATE;
      m2 += em2;
    });
    result[sub] = { m2, hours, litres, entryCount: entries.length };
  });
  return result;
}
